// src/features/auth/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storageSession from 'redux-persist/lib/storage/session'; // 使用 sessionStorage
import API from '../../api/axios';


const extractErrorMessage = (error) => {
    // Axios: error.response?.data 可能是 string / object / array
    const data = error?.response?.data;
    if (typeof data === 'string') return data;
    if (Array.isArray(data?.message)) return data.message.join(', ');
    if (typeof data?.message === 'string') return data.message;
    if (typeof data?.error === 'string') return data.error;
    if (error?.message) return error.message;
    return 'login failed';
};

export const normalizeCompanyID = (value) => {
    if (value === 0 || value === '0') {
        return 0;
    }

    if (typeof value === 'string') {
        const normalized = value.trim();

        if (normalized) {
            return normalized;
        }
    }

    return null;
};

// **登录**
export const login = createAsyncThunk(
    'auth/login',
    async ({ email, password, rememberMe }, { rejectWithValue, dispatch }) => {
        try {
            const response = await API.post('/auth/login', { email, password, rememberMe });
            console.log('Attempting to login with:', response.data);

            const payload = {
                user: {
                    email: response.data.email,
                    role: response.data.role,
                    status: response.data.status,
                    defaultLocale: response.data.defaultLocale,
                    firstName: response.data.firstName,
                    lastName: response.data.lastName,
                },
                accessToken: response.data.accessToken,
                companyID: response.data.companyID,
                allCompanies: response.data.allCompanies
            };
            // 后端返回的所有字段都保留
            return payload;
        } catch (error) {
            return rejectWithValue(extractErrorMessage(error));
        }
    });
// auth thunks

export const serverLogout = createAsyncThunk(
    'auth/serverLogout',
    async (_, { rejectWithValue }) => {
        try {
            // 尝试带鉴权退出
            await API.post('/auth/logout');
        } catch {
            // 两令牌都失效时走无鉴权
            try {
                await API.post('/auth/logout-anyway');
            } catch (e2) {
                return rejectWithValue(e2?.response?.data || 'server logout failed');
            }
        }
        return { ok: true };
    }
);

export const requestPasswordReset = createAsyncThunk(
    'auth/requestPasswordReset',
    async (email, { rejectWithValue }) => {
        try {
            const res = await API.post('/auth/forgot-password', { email }); // axios
            return res.data; // ✅ axios 用 data
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const resetPassword = createAsyncThunk(
    'auth/resetPassword',
    async ({ token, newPassword }, { rejectWithValue }) => {
        try {
            const res = await API.post('/auth/reset-password', { token, newPassword });
            return res.data;
        } catch (e) {
            return rejectWithValue(e.message);
        }
    }
);

export const refreshAccessToken = createAsyncThunk(
    'auth/refreshAccessToken',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await API.post('/auth/refresh');
            if (!data?.accessToken) return rejectWithValue('No accessToken');
            return data.accessToken;
        } catch (e) {
            return rejectWithValue(e?.response?.data || e.message || 'refresh failed');
        }
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState: {
        user: null,
        loading: false,
        error: null,
        companyID: null,
        allCompanies: [],
        accessToken: null,
        isAuthenticated: false,
        bootstrapped: false,
    },
    reducers: {
        // 如果需要定义同步 action，可在此添加
        updateAccessToken: (state, action) => {
            state.accessToken = action.payload; // 只更新 accessToken
            state.isAuthenticated = !!action.payload;
        },
        setCompanyID: (state, action) => {
            const value = action.payload;

            if (
                value === 0 ||
                value === '0'
            ) {
                console.log(
                    'Setting companyID to:',
                    0,
                );

                state.companyID = 0;
                return;
            }

            if (
                typeof value !== 'string' ||
                !value.trim()
            ) {
                console.error(
                    'Rejected invalid companyID:',
                    value,
                );

                return;
            }

            const normalizedCompanyID =
                value.trim();

            console.log(
                'Setting companyID to:',
                normalizedCompanyID,
            );

            state.companyID =
                normalizedCompanyID;
        },
        setUser: (state, action) => {
            state.user = action.payload;
        },
        setAllCompanies: (state, action) => {
            state.allCompanies = action.payload;
        },
        logout: (state) => {
            state.user = null;
            state.companyID = null;
            state.allCompanies = [];
            state.accessToken = null;
            state.isAuthenticated = false;
            state.bootstrapped = true; // ✅ 登出后鉴权检查视为已完成
            console.log('User logged out, clearing auth state.');

        },
        setBootstrapped: (state, action) => {
            state.bootstrapped = !!action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // checkAuth
            // login
            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                console.log('Login successful:', action.payload);
                state.loading = false;
                state.user = action.payload.user;
                const normalizedCompanyID =
                    normalizeCompanyID(
                        action.payload.companyID,
                    );

                const isAdmin =
                    action.payload.user?.role === 'ADMIN';

                state.companyID =
                    normalizedCompanyID ??
                    (isAdmin ? 0 : null);
                state.allCompanies = action.payload.allCompanies || []; // ✅ 保存 suppliers（普通用户为空数组）
                state.accessToken = action.payload.accessToken;
                state.isAuthenticated = true;
            })
            .addCase(login.rejected, (state, action) => {
                state.loading = false;
                state.user = null;
                state.companyID = null;
                state.allCompanies = [];      // ✅ 清空
                state.accessToken = null;
                state.error = typeof action.payload === 'string' ? action.payload : 'login failed';
            })
            .addCase(refreshAccessToken.fulfilled, (state, action) => {
                state.accessToken = action.payload;
                state.isAuthenticated = true;
            })
            .addCase(refreshAccessToken.rejected, (state) => {
                state.accessToken = null;
                state.isAuthenticated = false;
            })
    },
});

export const { updateAccessToken, setBootstrapped, setCompanyID, setUser, setAllCompanies, accessToken, logout } = authSlice.actions;

// **持久化配置**
const persistConfig = {
    key: 'auth',
    storage: storageSession, // ✅ 存储在 sessionStorage，防止长期存储 token
    whitelist: ['user', 'companyID', 'allCompanies'], // ✅ 仅存储这些字段
};
export const selectAuthUser = (state) =>
    state.auth.user;

export const selectCompanyID = (state) =>
    state.auth.companyID;

export const selectAllCompanies = (state) =>
    state.auth.allCompanies || [];

export const selectIsAdmin = (state) =>
    state.auth.user?.role === 'ADMIN';

export const selectIsAuthenticated = (state) =>
    state.auth.isAuthenticated;

export default persistReducer(persistConfig, authSlice.reducer);


